import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Loader2, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { Button } from "@/components/ui/button";
import { ButtonTip } from "@/components/ui/button-tip";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { OnboardingApiError, type FilePreview } from "../OnboardingAPI";
import { usePrefersReducedMotion } from "./calm";

/** What the page needs to look into a stored file: the controller's `preview`. */
export interface PreviewApi {
  load: (fileId: string) => Promise<FilePreview>;
  content: (fileId: string) => Promise<Blob>;
}

/** The extensions the "Ansehen" button is offered for (from the name; the server's `none` is the fallback). */
const TABLE_EXTENSIONS = new Set(["csv", "tsv", "txt", "xls", "xlsx"]);
const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "webp"]);

const extensionOf = (name: string) => (name.includes(".") ? name.split(".").pop()!.toLowerCase() : "");

export const canPreview = (name: string): boolean => {
  const extension = extensionOf(name);
  return TABLE_EXTENSIONS.has(extension) || IMAGE_EXTENSIONS.has(extension) || extension === "pdf";
};

/** A cell is only ever text, and never longer than this (the server cuts as well). */
const CELL_MAX = 500;
const cellText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const text = typeof value === "string" ? value : typeof value === "object" ? JSON.stringify(value) : String(value);
  return text.length > CELL_MAX ? `${text.slice(0, CELL_MAX)}…` : text;
};

type Loaded =
  | { state: "loading" }
  | { state: "error"; kind: "failed" | "rate_limited" }
  | { state: "table"; preview: Extract<FilePreview, { kind: "table" }> }
  | { state: "image"; url: string }
  | { state: "pdf"; url: string }
  | { state: "none"; reason: "unsupported" | "unreadable" };

/**
 * The look into a stored file, before anything is sent: what the server really holds (so it also holds after a reload).
 * A table is shown as a scrollable table with a fixed header row, an image as an image, a PDF as a button that opens it
 * in a new tab (the bytes are fetched as a blob; the address is released when the dialog closes). Everything that comes
 * from the file - cells, the file name - is rendered as text, never as HTML.
 */
export default function FilePreviewDialog({
  name,
  fileId,
  api,
  onClose,
}: {
  name: string;
  fileId: string;
  api: PreviewApi;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });
  const [attempt, setAttempt] = useState(0);
  const urls = useRef<string[]>([]);
  const releaseUrls = () => {
    urls.current.forEach((url) => URL.revokeObjectURL(url));
    urls.current = [];
  };

  const close = useCallback(() => {
    releaseUrls();
    onClose();
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoaded({ state: "loading" });
    const blobUrl = (blob: Blob) => {
      const url = URL.createObjectURL(blob);
      urls.current.push(url);
      return url;
    };
    (async () => {
      try {
        const preview = await api.load(fileId);
        if (cancelled) return;
        if (preview.kind === "table") setLoaded({ state: "table", preview });
        else if (preview.kind === "none") setLoaded({ state: "none", reason: preview.reason === "unreadable" ? "unreadable" : "unsupported" });
        else {
          const blob = await api.content(fileId);
          if (cancelled) return;
          const url = blobUrl(blob);
          setLoaded({ state: preview.contentType === "application/pdf" || blob.type === "application/pdf" ? "pdf" : "image", url });
        }
      } catch (error) {
        if (cancelled) return;
        if (error instanceof OnboardingApiError && (error.code === "locked" || error.code === "token_expired" || error.code === "invalid_token")) {
          // the lead changed under us: the page shows the status scene (or the problem page); nothing to show here
          close();
          return;
        }
        setLoaded({ state: "error", kind: error instanceof OnboardingApiError && error.code === "rate_limited" ? "rate_limited" : "failed" });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, attempt]);

  // the address of a blob does not outlive the dialog
  useEffect(() => releaseUrls, []);

  const preview = loaded.state === "table" ? loaded.preview : null;
  const columns = preview ? preview.headers.length : 0;

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent
        showClose={false}
        data-testid="file-preview"
        className={
          "flex max-h-[88dvh] w-[min(96vw,64rem)] max-w-none flex-col gap-3 p-5 sm:p-6 " +
          "max-sm:left-0 max-sm:top-0 max-sm:h-[100dvh] max-sm:max-h-none max-sm:w-full max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none " +
          (reduced ? "!animate-none !duration-0" : "")
        }
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-base font-semibold leading-6 [overflow-wrap:anywhere]" data-testid="preview-title">
              {preview ? t("onboardingPage.preview.titleTable", { name, rows: preview.totalRows, columns }) : name}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">{t("onboardingPage.preview.description")}</DialogDescription>
          </div>
          <ButtonTip label={t("onboardingPage.tips.previewClose")}>
          <DialogPrimitive.Close
            aria-label={t("onboardingPage.preview.close")}
            className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </DialogPrimitive.Close>
          </ButtonTip>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden" aria-live="polite" data-testid="preview-body">
          {loaded.state === "loading" && (
            <div className="space-y-2" role="status" data-testid="preview-loading">
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                {t("onboardingPage.preview.loading")}
              </p>
              <div className="space-y-1.5" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((row) => (
                  <div key={row} className="h-6 animate-pulse rounded bg-muted motion-reduce:animate-none" style={{ width: `${96 - row * 9}%` }} />
                ))}
              </div>
            </div>
          )}

          {loaded.state === "error" && (
            <div className="space-y-3 py-6 text-center" data-testid="preview-error">
              <p className="text-sm">{loaded.kind === "rate_limited" ? t("onboardingPage.preview.wait") : t("onboardingPage.preview.failed")}</p>
              <ButtonTip label={t("onboardingPage.tips.previewRetry")}>
                <Button type="button" variant="outline" onClick={() => setAttempt((n) => n + 1)} data-testid="preview-retry">
                  {t("onboardingPage.preview.retry")}
                </Button>
              </ButtonTip>
            </div>
          )}

          {loaded.state === "none" && (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="preview-none">
              {loaded.reason === "unreadable" ? t("onboardingPage.preview.unreadable") : t("onboardingPage.preview.unsupported")}
            </p>
          )}

          {preview && (
            <div className="flex h-full min-h-0 flex-col gap-2" data-testid="preview-table-box">
              {preview.sheetName && <p className="text-xs text-muted-foreground">{t("onboardingPage.preview.sheet", { name: preview.sheetName })}</p>}
              {preview.truncated && (
                <p className="text-xs text-muted-foreground" data-testid="preview-truncated">
                  {t("onboardingPage.preview.truncated", { count: preview.rows.length })}
                </p>
              )}
              <div
                className="min-h-0 flex-1 overflow-auto rounded-xl border border-border"
                role="region"
                tabIndex={0}
                aria-label={t("onboardingPage.preview.tableLabel", { name })}
                data-testid="preview-scroll"
              >
                <table className="w-max min-w-full border-separate border-spacing-0 text-left text-xs tabular-nums">
                  <thead>
                    <tr>
                      {preview.headers.map((header, index) => (
                        <th
                          key={index}
                          scope="col"
                          title={cellText(header)}
                          className="sticky top-0 z-[1] max-w-[16rem] truncate whitespace-nowrap border-b border-border bg-muted px-3 py-2 font-medium"
                        >
                          {cellText(header)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="odd:bg-muted/30">
                        {preview.headers.map((_, columnIndex) => {
                          const text = cellText(row?.[columnIndex]);
                          return (
                            <td key={columnIndex} title={text} className="max-w-[16rem] truncate whitespace-nowrap border-b border-border/60 px-3 py-1.5">
                              {text}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {loaded.state === "image" && (
            <div className="flex h-full items-center justify-center overflow-auto" data-testid="preview-image-box">
              <img src={loaded.url} alt={name} className="max-h-[70dvh] max-w-full rounded-lg border border-border object-contain" data-testid="preview-image" />
            </div>
          )}

          {loaded.state === "pdf" && (
            <div className="space-y-3 py-6 text-center" data-testid="preview-pdf">
              <p className="text-sm text-muted-foreground">{t("onboardingPage.preview.pdfHint")}</p>
              <ButtonTip label={t("onboardingPage.tips.openPdf")}>
                <Button type="button" onClick={() => window.open(loaded.url, "_blank", "noopener")} data-testid="preview-open-pdf">
                  <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t("onboardingPage.preview.openPdf")}
                </Button>
              </ButtonTip>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
